import { useCurrentStateAndParams } from "@uirouter/react";

import { filterFactory } from "@similarweb/pro-logic-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import {
    CLEARED_DATA_FILTER_URL_PARAMS,
    QUICK_FILTER_CONFIGS,
    QUICK_FILTER_NAMES,
    QUICK_FILTER_TRACKING_CATEGORY,
    type QuickFilterId,
} from "./consts";
import type { IQuickFilterState, FilterCounts } from "./types";

const QuickFilter = filterFactory.createTypedFilters<IQuickFilterState>();
const QUICK_FILTER_COUNT_KEYS: Record<QuickFilterId, string> = {
    "mention-wins": "mentionWinsCount",
    "mention-gap": "mentionGapCount",
    "citation-wins": "citationWinsCount",
    "citation-gap": "citationGapCount",
};

export interface IPromptsQuickFiltersProps {
    hasCompetitors: boolean;
    isLoading: boolean;
    counts?: FilterCounts;
}

export const PromptsQuickFilters = ({ hasCompetitors, isLoading, counts }: IPromptsQuickFiltersProps) => {
    const {
        params: { quickFilter },
    } = useCurrentStateAndParams();

    const promptsFieldLabel = translate("llm.prompts.quick_filters.field_label");
    const isFilterAvailable = (filterId: QuickFilterId): boolean => {
        if (isLoading || (QUICK_FILTER_CONFIGS[filterId].disabledWithoutCompetitors && !hasCompetitors)) {
            return false;
        }

        const countKey = QUICK_FILTER_COUNT_KEYS[filterId];
        const filterCount = (counts?.[countKey] as number) ?? 0;
        return filterCount > 0;
    };

    const handleQuickFilterChange = (state: IQuickFilterState) => {
        const { quickFilterId } = state;
        const filterName = quickFilterId ? QUICK_FILTER_NAMES[quickFilterId] : "";
        const isOn = !!quickFilterId;

        allTrackers.dispatchTrackEvent(QUICK_FILTER_TRACKING_CATEGORY, "Click", `${filterName}/${isOn ? "on" : "off"}`);

        if (!quickFilterId) {
            void proNavigator.updateParams(
                {
                    quickFilter: undefined,
                    ...CLEARED_DATA_FILTER_URL_PARAMS,
                },
                { reload: false },
            );
            return;
        }

        const config = QUICK_FILTER_CONFIGS[quickFilterId];

        void proNavigator.updateParams(
            {
                quickFilter: quickFilterId,
                ...CLEARED_DATA_FILTER_URL_PARAMS,
                ...config.preset,
            },
            { reload: false },
        );
    };

    const noCompetitorsTooltip = translate("llm.prompts.quick_filters.no_competitors_tooltip");

    return (
        <QuickFilter.Provider
            state={{ quickFilterId: quickFilter }}
            onChange={handleQuickFilterChange}
        >
            <Flex
                gap="8px"
                flexWrap="wrap"
                data-automation="prompts-quick-filters"
            >
                <QuickFilter.QuickFilter
                    isLoading={isLoading}
                    title={translate("llm.prompts.quick_filters.mention_wins.title")}
                    tooltip={translate("llm.prompts.quick_filters.mention_wins.tooltip")}
                    fieldLabel={promptsFieldLabel}
                    value={{ quickFilterId: "mention-wins" }}
                    isDisabled={!isFilterAvailable("mention-wins")}
                    fieldText={!isLoading ? (counts?.mentionWinsCount ?? 0).toLocaleString() : undefined}
                    data-automation="prompts-quick-filter-mention-wins"
                />
                <QuickFilter.QuickFilter
                    isLoading={isLoading}
                    title={translate("llm.prompts.quick_filters.mention_gap.title")}
                    tooltip={
                        hasCompetitors
                            ? translate("llm.prompts.quick_filters.mention_gap.tooltip")
                            : noCompetitorsTooltip
                    }
                    fieldLabel={promptsFieldLabel}
                    value={{ quickFilterId: "mention-gap" }}
                    isDisabled={!isFilterAvailable("mention-gap")}
                    fieldText={!isLoading ? (counts?.mentionGapCount ?? 0).toLocaleString() : undefined}
                    data-automation="prompts-quick-filter-mention-gap"
                />
                <QuickFilter.QuickFilter
                    isLoading={isLoading}
                    title={translate("llm.prompts.quick_filters.citation_wins.title")}
                    tooltip={translate("llm.prompts.quick_filters.citation_wins.tooltip")}
                    fieldLabel={promptsFieldLabel}
                    value={{ quickFilterId: "citation-wins" }}
                    isDisabled={!isFilterAvailable("citation-wins")}
                    fieldText={!isLoading ? (counts?.citationWinsCount ?? 0).toLocaleString() : undefined}
                    data-automation="prompts-quick-filter-citation-wins"
                />
                <QuickFilter.QuickFilter
                    isLoading={isLoading}
                    title={translate("llm.prompts.quick_filters.citation_gap.title")}
                    tooltip={
                        hasCompetitors
                            ? translate("llm.prompts.quick_filters.citation_gap.tooltip")
                            : noCompetitorsTooltip
                    }
                    fieldLabel={promptsFieldLabel}
                    value={{ quickFilterId: "citation-gap" }}
                    isDisabled={!isFilterAvailable("citation-gap")}
                    fieldText={!isLoading ? (counts?.citationGapCount ?? 0).toLocaleString() : undefined}
                    data-automation="prompts-quick-filter-citation-gap"
                />
            </Flex>
        </QuickFilter.Provider>
    );
};
